import { RIP_CODE_REGEX } from '@threadheads/shared';
import type { PrintSizeCodeOption, PrintSizeCodeComboboxStatus } from './PrintSizeCodeCombobox';

/**
 * Several store names can map to ONE code ("Tee" and "T-Shirt" both → TEE).
 * Show up to three, then count the rest, so a row stays one line. Shared by
 * every org-code pick-list so the label shape is identical across surfaces.
 */
export const STORE_NAME_CAP = 3;
export const joinStoreNames = (names: string[]): string =>
  names.length <= STORE_NAME_CAP
    ? names.join(' / ')
    : `${names.slice(0, STORE_NAME_CAP).join(' / ')} +${names.length - STORE_NAME_CAP}`;


interface ProductTypeCodeRow {
  code: string;
  storeName: string;
  isCustom: boolean;
  isSingleSize: boolean;
}

/** Group the selected org's store vocabulary once for both rows and pickers. */
export function buildProductTypeCodeOptions(
  rows: readonly ProductTypeCodeRow[] | undefined,
  mappedCodes: readonly string[],
  labels: { custom: string; singleSize: string; alreadyAdded: string; invalidCode: string },
  normalizeLabelCode: (code: string) => string = (code) => code,
): { typeOptions: PrintSizeCodeOption[]; typeStoreNamesByCode: Map<string, string[]> } {
  const byCode = new Map<string, { names: Set<string>; isCustom: boolean; isSingleSize: boolean }>();
  const namesByLabelCode = new Map<string, Set<string>>();
  for (const row of rows ?? []) {
    const code = row.code.trim().toUpperCase();
    if (!code) continue;
    const acc = byCode.get(code) ?? { names: new Set<string>(), isCustom: false, isSingleSize: false };
    acc.names.add(row.storeName);
    acc.isCustom ||= row.isCustom;
    acc.isSingleSize ||= row.isSingleSize;
    byCode.set(code, acc);
    const key = normalizeLabelCode(code);
    if (key) {
      const names = namesByLabelCode.get(key) ?? new Set<string>();
      names.add(row.storeName);
      namesByLabelCode.set(key, names);
    }
  }
  const sorted = (names: Set<string>): string[] => [...names].sort((a, b) => a.localeCompare(b));
  return {
    typeStoreNamesByCode: new Map([...namesByLabelCode].map(([code, names]) => [code, sorted(names)])),
    typeOptions: [...byCode].map(([code, acc]) => {
      const invalid = !RIP_CODE_REGEX.test(code);
      const alreadyAdded = mappedCodes.includes(code);
      return {
        code,
        label: joinStoreNames(sorted(acc.names)),
        badges: [...(acc.isCustom ? [labels.custom] : []), ...(acc.isSingleSize ? [labels.singleSize] : [])],
        disabled: invalid || alreadyAdded,
        disabledReason: invalid ? labels.invalidCode : alreadyAdded ? labels.alreadyAdded : undefined,
      };
    }).sort((a, b) => a.code.localeCompare(b.code)),
  };
}

export function codeTablesStatus(
  query: { isLoading: boolean; isError: boolean },
  optionCount: number,
): PrintSizeCodeComboboxStatus {
  return query.isLoading ? 'loading' : query.isError ? 'error' : optionCount === 0 ? 'empty' : 'loaded';
}
